/**
 * ตัวกรอง picker อุปกรณ์ — ตัดเครื่องที่ "ยังมีใบตามเก็บค้าง" ออกจากตัวเลือก (ด่าน L2 ฝั่งจอ)
 *
 * R4U-2 (รอบพิสูจน์ที่ 4, 8 ก.ย. 2026): backend ส่ง `has_pending_return` / `pending_return_count`
 * มาครบทุกพื้นผิวที่เสิร์ฟอุปกรณ์แล้ว (a1c9a20) และ picker ใบงาน/ใบเบิก/ค้นหาเปลี่ยนอุปกรณ์กรองฝั่ง
 * server อยู่แล้ว **แต่ฟอร์มสร้าง/แก้ไขการเช่า และ picker "เครื่องใหม่" ในโมดัลเปลี่ยนอุปกรณ์ ใส่ทั้ง
 * list ลง state ไม่กรอง** → ผู้ใช้เลือกเครื่องที่ยังอยู่กับลูกค้า (สถานะ drift เป็น AVAILABLE) ได้
 * แล้วไปโดน 400 จากด่าน L2 ตอนกดบันทึก = dead-end แบบเดียวกับที่ B4b เพิ่งแก้ให้ของนับจำนวน
 *
 * กติกา:
 * - เครื่องที่ `has_pending_return` เป็นจริง → **ไม่เสนอ** (ซ่อน ไม่ใช่ disabled — parity กับ picker
 *   ใบงาน/ใบเบิกที่ backend ตัดออกตั้งแต่ต้น · ผู้ใช้ที่พิมพ์ค้นแล้วไม่เจอ ให้โมดัลเปลี่ยนอุปกรณ์อธิบายผ่าน
 *   `emptyMessage` ด้วย `pendingReturnReason`)
 * - `keepIds` = เครื่องที่อยู่ในสัญญา/ฟอร์มใบนี้อยู่แล้ว → **คงไว้เสมอ** ไม่ว่าธงจะเป็นอะไร เพราะแถวเดิม
 *   ของฟอร์มแก้ไขต้องยังแสดงค่าที่เลือกไว้ได้ (ตัดทิ้ง = ช่องกลับเป็น "-- เลือกอุปกรณ์ --" ทั้งที่
 *   สัญญาถือเครื่องนั้นอยู่จริง — บั๊กคลาสเดียวกับ /rentals/edit/90) · ใบตามเก็บของสัญญาใบนี้เอง
 *   ไม่ใช่เหตุให้ห้ามแก้แถวของตัวเอง (ด่าน L2 ฝั่ง backend ก็ดูเฉพาะใบของ "สัญญาอื่น")
 * - ธงว่าง/undefined (payload เก่า) = ถือว่าไม่มีใบค้าง (fail-open เหมือน backend ที่ default False)
 */

export interface PendingReturnLike {
  id: number;
  has_pending_return?: boolean | null;
  pending_return_count?: number | null;
}

type IdLike = number | string | '' | null | undefined;

/**
 * true = เครื่องนี้ยังมีรายการตามเก็บที่ยังไม่ปิด (PENDING/SCHEDULED/IN_PROGRESS)
 * รับ object อะไรก็ได้ (payload อุปกรณ์แต่ละหน้ามี type ท้องถิ่นของตัวเอง — บางตัวยังไม่ประกาศธงนี้)
 */
export function hasPendingReturn(device: object | null | undefined): boolean {
  return (device as Partial<PendingReturnLike> | null | undefined)?.has_pending_return === true;
}

const toIdSet = (ids: Iterable<IdLike>): Set<number> => {
  const set = new Set<number>();
  for (const id of ids) {
    const n = typeof id === 'number' ? id : Number(id);
    if (Number.isFinite(n) && n > 0) set.add(n);
  }
  return set;
};

/**
 * ตัดเครื่องที่มีใบตามเก็บค้างออก ยกเว้น id ใน `keepIds` (เครื่องที่อยู่ในสัญญา/ฟอร์มนี้อยู่แล้ว)
 * — คืน array ใหม่เสมอ ไม่แก้ของเดิม · ลำดับคงเดิม
 */
export function excludePendingReturn<T extends PendingReturnLike>(
  devices: readonly T[],
  keepIds: Iterable<IdLike> = [],
): T[] {
  const keep = toIdSet(keepIds);
  return devices.filter((d) => !hasPendingReturn(d) || keep.has(d.id));
}

/**
 * ข้อความอธิบายว่าทำไมเครื่องนี้ "มีอยู่แต่เลือกไม่ได้" — ใช้ใน emptyMessage ของ picker
 * คืน null เมื่อเครื่องไม่มีใบค้าง (ผู้เรียกไปเช็คเหตุอื่นต่อ)
 */
export function pendingReturnReason(
  device: PendingReturnLike | null | undefined,
  name?: string,
): string | null {
  if (!hasPendingReturn(device)) return null;
  const n = device?.pending_return_count || 0;
  const who = (name ?? '').trim() || 'เครื่องนี้';
  const count = n > 0 ? ` ${n} รายการ` : '';
  return (
    `พบ ${who} แต่เลือกไม่ได้ — ยังมีรายการตามเก็บที่ยังไม่ปิด${count} (รอรับคืน · ของอาจยังอยู่กับลูกค้า) · ` +
    'บันทึกรับคืนที่เมนู "ติดตามการคืนอุปกรณ์" หรือยกเลิกรายการตามเก็บก่อน แล้วกลับมาเลือกใหม่'
  );
}
